/**
 * The signed-in user's collection: the owned toggle on a book or system page, and the collection
 * section of the account page. What counts as owned is decided on the server (services/collection.ts).
 */
import { useState } from 'react'
import { Link } from 'react-router'
import { BookCheck, BookPlus } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { num, plural } from '../lib/format'
import { invalidate, useQuery } from '../lib/query'
import { useSession } from '../lib/session'
import type { CollectionStatus } from '../../server/services/collection'

const PATHS = { book: 'books', system: 'systems' } as const

/** Where a synced mark came from, for people. */
const SOURCE_LABELS: Record<string, string> = { grimoire: 'Grimoire' }

/**
 * "Mark as owned" on a book or system page. A record owned only through its books (or variants) is
 * owned already, so it shows that instead of offering to mark it.
 */
export function OwnedButton({ type, id }: { type: 'book' | 'system'; id: string }) {
  const session = useSession()
  const key = session.user ? `/api/v1/me/collection/${PATHS[type]}/${id}` : null
  const { data } = useQuery<CollectionStatus>(key)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  if (!key || !data) return null

  const toggle = async () => {
    setBusy(true)
    setErr('')
    try {
      await api(key, { method: data.marked ? 'DELETE' : 'PUT' })
      // Owning one record changes what its systems, variants and every list say.
      invalidate('/api/v1/me/collection', '/api/v1/books', '/api/v1/systems', '/api/v1/series')
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Could not update your collection')
    } finally {
      setBusy(false)
    }
  }

  const synced = data.sources.filter((s) => s !== 'manual').map((s) => SOURCE_LABELS[s] ?? s)
  if (!data.marked && data.owned) {
    const how =
      type === 'book'
        ? `You own ${data.via === 1 ? 'a variant' : `${num(data.via)} variants`} of this book`
        : data.via > 0
          ? `You own ${plural(data.via, 'book')} for this system`
          : 'You own a system inside this one'
    return (
      <span className="owned-note">
        <BookCheck size={16} aria-hidden="true" /> {how}
      </span>
    )
  }
  return (
    <>
      <button
        type="button"
        className={`button button-quiet${data.marked ? ' is-owned' : ''}`}
        aria-pressed={data.marked}
        disabled={busy}
        onClick={() => void toggle()}
        title={
          synced.length
            ? `Kept in sync by ${synced.join(' and ')}. A later sync adds it back unless you unlink it there.`
            : undefined
        }
      >
        {data.marked ? <BookCheck size={16} aria-hidden="true" /> : <BookPlus size={16} aria-hidden="true" />}{' '}
        {data.marked ? 'Owned' : 'Mark as owned'}
      </button>
      {err && (
        <p className="field-error" role="alert">
          {err}
        </p>
      )}
    </>
  )
}

/** The account page's collection section: counts, links to browse it, and who can see it. */
export function MyCollection({ save }: { save: (patch: Record<string, unknown>, msg: string) => Promise<void> }) {
  const { data } = useQuery<{ public: boolean; books: number; systems: number }>('/api/v1/me/collection')
  if (!data) return null
  return (
    <section className="section">
      <h2 className="section-title">Your collection</h2>
      <p>
        You own <Link to="/books?owned=1">{plural(data.books, 'book')}</Link> and{' '}
        <Link to="/systems?owned=1">{plural(data.systems, 'system')}</Link>. Mark a book as owned on its page, or let
        Grimoire keep your collection in sync: turn on collection sync in its Grimoire Codex DB settings, with an API
        token from this account.
      </p>
      <label className="toggle-field">
        <input
          type="checkbox"
          checked={data.public}
          onChange={(e) =>
            void save(
              { collection_public: e.target.checked },
              e.target.checked ? 'Your collection is now on your public profile.' : 'Your collection is now private.',
            )
          }
        />
        <span>
          <strong>Show my collection on my profile</strong>
          <span className="field-hint">Off by default. When on, anyone can see and browse what you own.</span>
        </span>
      </label>
    </section>
  )
}

/** A public profile's collection line, when its owner shares it. */
export function ProfileCollection({
  userId,
  collection,
}: {
  userId: string
  collection: { books: number; systems: number }
}) {
  return (
    <p className="notice">
      <BookCheck size={18} aria-hidden="true" />
      <span>
        Owns <Link to={`/books?owned=1&collector=${userId}`}>{plural(collection.books, 'book')}</Link> and{' '}
        <Link to={`/systems?owned=1&collector=${userId}`}>{plural(collection.systems, 'system')}</Link>
      </span>
    </p>
  )
}
